import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Send, Trash2, Edit2, X, Sparkles, Share2, Image as ImageIcon, Video, Upload, Mail, Bell, UserPlus, Bookmark } from 'lucide-react';
import { format } from 'date-fns';
import { Mailbox } from './Mailbox';
import { NotificationBell } from './NotificationBell';

interface MediaFile {
  url: string;
  type: 'image' | 'video';
  name: string;
}

interface Post {
  id: string;
  user_id: string;
  title: string;
  content: string;
  category: string;
  created_at: string;
  media_files?: MediaFile[];
  profiles?: {
    display_name: string;
  };
  reaction_count?: number;
  comment_count?: number;
  user_reacted?: boolean;
  is_following?: boolean;
  is_following_user?: boolean;
}

interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles?: {
    display_name: string;
  };
  reaction_count?: number;
  user_reacted?: boolean;
}

export const ConnectedCollective = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [showNewPost, setShowNewPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [showComments, setShowComments] = useState<string | null>(null);
  const [showMailbox, setShowMailbox] = useState(false);
  const [messageRecipientId, setMessageRecipientId] = useState<string | null>(null);
  const [messageRecipientName, setMessageRecipientName] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { value: 'general', label: 'General Support' },
    { value: 'mental-health', label: 'Mental Health' },
    { value: 'spiritual-growth', label: 'Spiritual Growth' },
    { value: 'tarot-intuition', label: 'Tarot & Intuition Practice' },
    { value: 'self-care', label: 'Self-Care Tips' },
    { value: 'success-stories', label: 'Success Stories' },
    { value: 'questions', label: 'Questions & Help' }
  ];

  useEffect(() => {
    getCurrentUser();
    loadPosts();
  }, []);

  useEffect(() => {
    if (user) {
      loadUnreadMessages();
    }
  }, [user]);

  useEffect(() => {
    if (selectedPost) {
      loadComments(selectedPost.id);
    }
  }, [selectedPost]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const loadUnreadMessages = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('private_messages')
        .select('id')
        .eq('recipient_id', user.id)
        .eq('is_read', false)
        .eq('deleted_by_recipient', false);

      if (error) throw error;
      setUnreadMessageCount(data?.length || 0);
    } catch (error: any) {
      console.error('Error loading unread messages:', error);
    }
  };

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('community_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      if (data) {
        const userIds = [...new Set(data.map(p => p.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);

        // Fetch reaction counts and user reactions
        const postIds = data.map(p => p.id);
        const { data: reactions } = await supabase
          .from('community_post_reactions')
          .select('post_id, user_id')
          .in('post_id', postIds);

        // Fetch comment counts
        const { data: commentCounts } = await supabase
          .from('community_comments')
          .select('post_id')
          .in('post_id', postIds);

        const postsWithProfiles = data.map(post => ({
          ...post,
          media_files: (post.media_files as any) || [],
          profiles: profiles?.find(p => p.user_id === post.user_id) || { display_name: 'Anonymous' },
          reaction_count: reactions?.filter(r => r.post_id === post.id).length || 0,
          user_reacted: reactions?.some(r => r.post_id === post.id && r.user_id === user?.id) || false,
          comment_count: commentCounts?.filter(c => c.post_id === post.id).length || 0
        }));

        setPosts(postsWithProfiles);
      } else {
        setPosts([]);
      }
    } catch (error: any) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (postId: string) => {
    try {
      const { data, error } = await supabase
        .from('community_comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch profiles separately
      if (data) {
        const userIds = [...new Set(data.map(c => c.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);

        const commentsWithProfiles = data.map(comment => ({
          ...comment,
          profiles: profiles?.find(p => p.user_id === comment.user_id) || { display_name: 'Anonymous' }
        }));

        setComments(commentsWithProfiles);
      } else {
        setComments([]);
      }
    } catch (error: any) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    }
  };

  const createPost = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to create a post');
      return;
    }
    
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const postData = {
        user_id: user.id,
        title: newPostTitle,
        content: newPostContent,
        category: selectedCategory,
        media_files: mediaFiles as any
      };

      const { error } = await supabase
        .from('community_posts')
        .insert([postData]);

      if (error) throw error;

      toast.success('Post created successfully!');
      setNewPostTitle('');
      setNewPostContent('');
      setMediaFiles([]);
      setShowNewPost(false);
      loadPosts();
    } catch (error: any) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingMedia(true);
    try {
      const uploadedFiles: MediaFile[] = [];

      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = fileName;

        const { error: uploadError, data } = await supabase.storage
          .from('community-media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('community-media')
          .getPublicUrl(filePath);

        const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
        uploadedFiles.push({
          url: publicUrl,
          type: mediaType,
          name: file.name
        });
      }

      setMediaFiles([...mediaFiles, ...uploadedFiles]);
      toast.success('Media uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    } finally {
      setUploadingMedia(false);
    }
  };

  const toggleReaction = async (postId: string) => {
    if (!user?.id) {
      toast.error('You must be logged in to react');
      return;
    }
    
    try {
      // Check if user already boosted
      const { data: existing } = await supabase
        .from('community_post_reactions')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .eq('reaction_type', 'boost')
        .maybeSingle();

      if (existing) {
        // Remove boost
        await supabase
          .from('community_post_reactions')
          .delete()
          .eq('id', existing.id);
        toast.success('Boost removed');
      } else {
        // Add boost
        await supabase
          .from('community_post_reactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            reaction_type: 'boost'
          });
        toast.success('Post boosted! 🌟');
      }

      loadPosts();
    } catch (error: any) {
      console.error('Error toggling boost:', error);
    }
  };

  const toggleCommentReaction = async (commentId: string) => {
    if (!user?.id) {
      toast.error('You must be logged in to react');
      return;
    }
    
    try {
      const { data: existing } = await supabase
        .from('community_comment_reactions')
        .select('id')
        .eq('comment_id', commentId)
        .eq('user_id', user.id)
        .eq('reaction_type', 'boost')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('community_comment_reactions')
          .delete()
          .eq('id', existing.id);
      } else {
        await supabase
          .from('community_comment_reactions')
          .insert({
            comment_id: commentId,
            user_id: user.id,
            reaction_type: 'boost'
          });
      }

      if (selectedPost) {
        loadComments(selectedPost.id);
      }
    } catch (error: any) {
      console.error('Error toggling comment boost:', error);
    }
  };

  const sharePost = async (post: Post) => {
    const shareUrl = `${window.location.origin}/connect-and-grow#post-${post.id}`;
    const shareText = `Check out this post: "${post.title}" on Connect & Grow`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        // User cancelled share
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const updatePost = async () => {
    if (!editingPostId || !editTitle.trim() || !editContent.trim()) return;

    try {
      const { error } = await supabase
        .from('community_posts')
        .update({
          title: editTitle,
          content: editContent
        })
        .eq('id', editingPostId);

      if (error) throw error;

      toast.success('Post updated successfully!');
      setEditingPostId(null);
      setEditTitle('');
      setEditContent('');
      loadPosts();
    } catch (error: any) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
    }
  };

  const deletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post? This will also delete all associated media.')) return;

    try {
      // First, get the post to access its media files
      const { data: post, error: fetchError } = await supabase
        .from('community_posts')
        .select('media_files')
        .eq('id', postId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!post) throw new Error('Post not found');

      // Delete media files from storage
      if (post?.media_files && Array.isArray(post.media_files)) {
        const mediaFiles = post.media_files as unknown as MediaFile[];
        for (const file of mediaFiles) {
          try {
            const filePath = file.url.split('/community-media/').pop();
            if (filePath) {
              await supabase.storage
                .from('community-media')
                .remove([filePath]);
            }
          } catch (storageError) {
            console.error('Error deleting media file:', storageError);
          }
        }
      }

      // Delete the post from database
      const { error } = await supabase
        .from('community_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      toast.success('Post and media deleted successfully');
      if (selectedPost?.id === postId) {
        setSelectedPost(null);
      }
      loadPosts();
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const deleteMediaFile = async (postId: string, fileUrl: string) => {
    if (!confirm('Are you sure you want to delete this media file?')) return;

    try {
      // Get the current post
      const { data: post, error: fetchError } = await supabase
        .from('community_posts')
        .select('media_files')
        .eq('id', postId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!post) throw new Error('Post not found');

      // Remove the file from storage
      const filePath = fileUrl.split('/community-media/').pop();
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('community-media')
          .remove([filePath]);

        if (storageError) throw storageError;
      }

      // Update the post's media_files array
      const currentMediaFiles = post.media_files as unknown as MediaFile[] | null;
      const updatedMediaFiles = (currentMediaFiles || []).filter(
        (file: MediaFile) => file.url !== fileUrl
      );

      const { error: updateError } = await supabase
        .from('community_posts')
        .update({ media_files: updatedMediaFiles as any })
        .eq('id', postId);

      if (updateError) throw updateError;

      toast.success('Media file deleted successfully');
      loadPosts();
      if (selectedPost?.id === postId) {
        const updatedPost = { ...selectedPost, media_files: updatedMediaFiles };
        setSelectedPost(updatedPost);
      }
    } catch (error: any) {
      console.error('Error deleting media file:', error);
      toast.error('Failed to delete media file');
    }
  };

  const addComment = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to comment');
      return;
    }
    
    if (!newComment.trim() || !selectedPost) return;

    try {
      const { error } = await supabase
        .from('community_comments')
        .insert({
          post_id: selectedPost.id,
          user_id: user.id,
          content: newComment
        });

      if (error) throw error;

      setNewComment('');
      loadComments(selectedPost.id);
      toast.success('Comment added!');
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const startEditPost = (post: Post) => {
    setEditingPostId(post.id);
    setEditTitle(post.title);
    setEditContent(post.content);
  };

  const startEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  const updateComment = async () => {
    if (!editingCommentId || !editCommentContent.trim()) return;

    try {
      const { error } = await supabase
        .from('community_comments')
        .update({ content: editCommentContent })
        .eq('id', editingCommentId);

      if (error) throw error;

      toast.success('Comment updated!');
      setEditingCommentId(null);
      setEditCommentContent('');
      if (selectedPost) {
        loadComments(selectedPost.id);
      }
    } catch (error: any) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId: string, commentUserId: string) => {
    // Allow deletion if user is comment author OR post author
    if (user?.id !== commentUserId && user?.id !== selectedPost?.user_id) {
      toast.error('You can only delete your own comments or comments on your posts');
      return;
    }

    if (!confirm('Are you sure you want to delete this comment?')) return;

    try {
      const { error } = await supabase
        .from('community_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      toast.success('Comment deleted');
      if (selectedPost) {
        loadComments(selectedPost.id);
        loadPosts(); // Refresh to update comment count
      }
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleSendMessage = (userId: string, userName: string) => {
    setMessageRecipientId(userId);
    setMessageRecipientName(userName);
    setShowMailbox(true);
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading community posts...</div>;
  }

  if (showMailbox) {
    return (
      <Mailbox 
        onClose={() => {
          setShowMailbox(false);
          setMessageRecipientId(null);
          setMessageRecipientName(null);
        }}
        recipientId={messageRecipientId || undefined}
        recipientName={messageRecipientName || undefined}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Connected Collective</h2>
          <p className="text-muted-foreground mt-2">Connect, share, and support each other on your wellness journey</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowMailbox(true)} className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Mailbox
          </Button>
          <Button onClick={() => setShowNewPost(!showNewPost)} className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {showNewPost ? 'Cancel' : 'Share Your Story'}
          </Button>
        </div>
      </div>

      {/* New Post Form */}
      {showNewPost && (
        <Card className="p-6 bg-gradient-therapy shadow-therapy border-primary/20">
          <h3 className="text-xl font-semibold text-foreground mb-4">Create a New Post</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-background text-foreground border border-border rounded-lg p-2"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              {/* Special message for Tarot & Intuition category */}
              {selectedCategory === 'tarot-intuition' && (
                <div className="mt-3 p-3 bg-accent/10 border border-accent/30 rounded-lg">
                  <p className="text-sm text-accent font-medium mb-1">✨ Tarot & Intuition Practice Space</p>
                  <p className="text-xs text-muted-foreground">
                    Share your tarot journey, ask for practice partners, request feedback on readings, or discuss card interpretations and intuitive development!
                  </p>
                </div>
              )}
            </div>
            <div>
              <Input
                placeholder="Post Title"
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
                className="bg-background"
              />
            </div>
            <div>
              <Textarea
                placeholder="Share your thoughts, experiences, or questions..."
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                className="min-h-[150px] bg-background"
              />
            </div>

            {/* Media Upload Section */}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleMediaUpload}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingMedia}
                  className="flex items-center gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  Add Photos
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingMedia}
                  className="flex items-center gap-2"
                >
                  <Video className="h-4 w-4" />
                  Add Videos
                </Button>
              </div>
              {uploadingMedia && (
                <p className="text-sm text-muted-foreground">Uploading media...</p>
              )}
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {mediaFiles.map((file, idx) => (
                    <div key={idx} className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                      {file.type === 'image' ? (
                        <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                      ) : (
                        <video src={file.url} className="w-full h-full object-cover" />
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute top-1 right-1 h-6 w-6 p-0"
                        onClick={() => setMediaFiles(mediaFiles.filter((_, i) => i !== idx))}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={createPost} className="w-full">
              <Send className="h-4 w-4 mr-2" />
              Post to Community
            </Button>
          </div>
        </Card>
      )}

      {/* Posts List */}
      <div className="grid gap-4">
        {posts.map((post) => (
          <Card key={post.id} className="hover:shadow-therapy transition-all duration-300">
            {editingPostId === post.id ? (
              <CardContent className="p-6 space-y-4">
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="bg-background"
                />
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[100px] bg-background"
                />
                <div className="flex gap-2">
                  <Button onClick={updatePost} size="sm">Save</Button>
                  <Button onClick={() => setEditingPostId(null)} size="sm" variant="outline">Cancel</Button>
                </div>
              </CardContent>
            ) : (
              <>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                          {categories.find(c => c.value === post.category)?.label || post.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(post.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <CardTitle className="text-xl text-foreground">{post.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-1">
                        Posted by {post.profiles?.display_name || 'Anonymous'}
                      </CardDescription>
                    </div>
                    {user?.id === post.user_id && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEditPost(post)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deletePost(post.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground whitespace-pre-wrap mb-4">{post.content}</p>

                  {/* Media Display */}
                  {post.media_files && post.media_files.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {post.media_files.map((file, idx) => (
                        <div key={idx} className="relative aspect-video bg-muted rounded-lg overflow-hidden group">
                          {file.type === 'image' ? (
                            <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                          ) : (
                            <video src={file.url} controls className="w-full h-full object-cover" />
                          )}
                          {user?.id === post.user_id && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => deleteMediaFile(post.id, file.url)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Interaction Buttons */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleReaction(post.id)}
                      className={`flex items-center gap-2 ${post.user_reacted ? 'text-primary' : ''}`}
                    >
                      <Sparkles className={`h-4 w-4 ${post.user_reacted ? 'fill-current' : ''}`} />
                      <span>{post.reaction_count || 0} Boosts</span>
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedPost(post);
                        setShowComments(post.id);
                      }}
                      className="flex items-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      <span>{post.comment_count || 0} {post.comment_count === 1 ? 'Comment' : 'Comments'}</span>
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => sharePost(post)}
                      className="flex items-center gap-2"
                    >
                      <Share2 className="h-4 w-4" />
                      Share
                    </Button>
                    
                    {user?.id !== post.user_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSendMessage(post.user_id, post.profiles?.display_name || 'User')}
                        className="flex items-center gap-2"
                      >
                        <Mail className="h-4 w-4" />
                        Message
                      </Button>
                    )}
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        ))}

        {posts.length === 0 && (
          <Card className="p-12 text-center">
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No posts yet</h3>
            <p className="text-muted-foreground mb-4">Be the first to share your story with the community!</p>
            <Button onClick={() => setShowNewPost(true)}>Create First Post</Button>
          </Card>
        )}
      </div>

      {/* Comments Dialog */}
      {selectedPost && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b border-border">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl">{selectedPost.title}</CardTitle>
                  <CardDescription className="mt-1">
                    by {selectedPost.profiles?.display_name || 'Anonymous'}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedPost(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
              <p className="text-foreground whitespace-pre-wrap mb-6">{selectedPost.content}</p>
              
              <div className="border-t border-border pt-4">
                <h4 className="font-semibold text-foreground mb-4">Comments ({comments.length})</h4>
                <div className="space-y-4 mb-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="bg-muted/30 rounded-lg p-4">
                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editCommentContent}
                            onChange={(e) => setEditCommentContent(e.target.value)}
                            className="bg-background"
                          />
                          <div className="flex gap-2">
                            <Button onClick={updateComment} size="sm">Save</Button>
                            <Button onClick={() => {
                              setEditingCommentId(null);
                              setEditCommentContent('');
                            }} size="sm" variant="outline">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">
                              {comment.profiles?.display_name || 'Anonymous'}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                              </span>
                              {user?.id === comment.user_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditComment(comment)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              )}
                              {(user?.id === comment.user_id || user?.id === selectedPost?.user_id) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteComment(comment.id, comment.user_id)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-foreground text-sm">{comment.content}</p>
                        </>
                      )}
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No comments yet. Be the first to comment!</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Textarea
                    placeholder="Add a supportive comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={addComment} size="sm">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};